import { useEffect, useRef, useState } from "react";
import { listMaterials, getMaterialVariantDimensions } from "../api/materials";
import { adjustInventory, listInventoryBalances } from "../api/inventory";
import MaterialVariantSelectModal from "../components/MaterialVariantSelectModal";
import StockCountExcelDialog from "../components/StockCountExcelDialog";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { EmptyState, PageHeader } from "../components/ui";

const ITEMS_PER_PAGE = 25;
const MATERIALS_PER_DROPDOWN_PAGE = 25;

const inventoryTableSx = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  "& .MuiTableCell-root": {
    px: 0.75,
    py: 0.7,
    fontSize: "0.7rem",
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: "1px solid #edf1f5",
  },
  "& .MuiTableCell-head": {
    py: 0.85,
    whiteSpace: "normal",
    wordBreak: "break-word",
    backgroundColor: "#ede9fe",
    color: "#3730a3",
    fontSize: "0.67rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
    borderBottom: "1px solid #ddd6fe",
  },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: "#fcfbff",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "#f5f3ff",
  },
};

function formatDateOnly(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

// Yarı mamul lotlarında birim, lot ön ekinden okunur. Yalnızca GÖSTERİM içindir;
// stok verisi ve hesaplar değişmez.
const LOT_PREFIX_UNITS = [
  ["KSM", "tabaka"],
  ["SHRK", "paket"],
  ["KOLI", "koli"],
];

function displayUnit(lotNo, materialUnit) {
  const lot = String(lotNo || "").trim().toUpperCase();
  const match = LOT_PREFIX_UNITS.find(([prefix]) => lot.startsWith(prefix));
  return match ? match[1] : (materialUnit || "");
}

function displayLotNo(lotNo) {
  const rawLotNo = String(lotNo || "").trim();
  return rawLotNo.replace(/\s*\/R\d+/i, "").trim();
}

// Miktarın üzerine gelince varyantı adlandırılmış olarak gösterir.
// (İş emirleri / siparişler / üretim raporu ekranlarındaki tooltip ile aynı desen.)
function stockQtyCell(item) {
  const details = Array.isArray(item?.variant_details) ? item.variant_details : [];
  const qty = (
    <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
      {Math.round(Number(item?.on_hand_qty || 0))}
    </Typography>
  );
  if (!details.length) return qty;

  return (
    <Tooltip
      arrow
      placement="left"
      title={
        <div>
          {item?.material_name ? (
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{item.material_name}</div>
          ) : null}
          {details.map((detail, index) => (
            <div key={`${detail?.label || "dim"}-${index}`} style={{ whiteSpace: "nowrap" }}>
              <span style={{ opacity: 0.75 }}>{detail?.label}:</span>{" "}
              <b>{detail?.value}{detail?.unit ? ` ${detail.unit}` : ""}</b>
            </div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{qty}</span>
    </Tooltip>
  );
}

function optionalFilter(value) {
  const s = String(value ?? "").trim();
  return s || undefined;
}

function currentUserRole() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.role || null;
  } catch {
    return null;
  }
}

export default function Inventory() {
  const role = currentUserRole();
  // Stoktan silme depocu rolüne özel; admin de bu butonu kullanabilir.
  const canDeleteStock = role === "depocu" || role === "admin";
  const [materials, setMaterials] = useState([]);

  const [filterMaterialId, setFilterMaterialId] = useState("");
  const [materialDropdownOpen, setMaterialDropdownOpen] = useState(false);
  const [materialDropdownPage, setMaterialDropdownPage] = useState(0);
  const [materialDropdownAnchor, setMaterialDropdownAnchor] = useState(null);
  const [filterVariantId, setFilterVariantId] = useState(undefined);
  const [filterVariantDisplay, setFilterVariantDisplay] = useState("");
  const [filterVariantTemplate, setFilterVariantTemplate] = useState([]);
  const [filterVariantModalOpen, setFilterVariantModalOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterColor, setFilterColor] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterLotNo, setFilterLotNo] = useState("");
  const [filterInboundDocNo, setFilterInboundDocNo] = useState("");
  const [filterSalesOrderNo, setFilterSalesOrderNo] = useState("");
  const [filterWorkOrderNo, setFilterWorkOrderNo] = useState("");
  const [filterBoxWidth, setFilterBoxWidth] = useState("");
  const [filterBoxLength, setFilterBoxLength] = useState("");
  const [filterBoxHeight, setFilterBoxHeight] = useState("");
  const [filterPaperGrammage, setFilterPaperGrammage] = useState("");
  const [filterPaperType, setFilterPaperType] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const filterChangedRef = useRef(false);

  useEffect(() => {
    listMaterials({ limit: 500 })
      .then(setMaterials)
      .catch((err) => {
        console.error("MATERIALS LOAD ERROR", err);
      });
  }, []);

  function loadBalances() {
    const materialId = filterMaterialId ? Number(filterMaterialId) : null;
    if (filterMaterialId && !Number.isFinite(materialId)) return;
    const pageToLoad = filterChangedRef.current ? 0 : page;
    if (filterChangedRef.current) filterChangedRef.current = false;
    setLoading(true);
    listInventoryBalances({
      material_id: materialId ?? undefined,
      variant_id: filterVariantId === undefined ? undefined : filterVariantId,
      color_name: optionalFilter(filterColor),
      customer_name: optionalFilter(filterCustomer),
      supplier_name: optionalFilter(filterSupplier),
      lot_no: optionalFilter(filterLotNo),
      inbound_doc_no: optionalFilter(filterInboundDocNo),
      sales_order_no: optionalFilter(filterSalesOrderNo),
      work_order_no: optionalFilter(filterWorkOrderNo),
      box_width: optionalFilter(filterBoxWidth),
      box_length: optionalFilter(filterBoxLength),
      box_height: optionalFilter(filterBoxHeight),
      paper_grammage: optionalFilter(filterPaperGrammage),
      paper_type: optionalFilter(filterPaperType),
      page: pageToLoad + 1,
      limit: ITEMS_PER_PAGE,
    })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((err) => {
        console.error("BALANCES LOAD ERROR", err);
        alert("Envanter yüklenemedi");
      })
      .finally(() => setLoading(false));
  }

  function getRowKey(item) {
    return `${item.material_id}:${item.material_variant_id || 0}:${item.lot_no || ""}`;
  }

  function toggleRow(item, checked) {
    const key = getRowKey(item);
    setSelectedRows((current) => {
      const next = { ...current };
      if (checked) next[key] = item;
      else delete next[key];
      return next;
    });
  }

  function toggleCurrentPage(checked) {
    setSelectedRows((current) => {
      const next = { ...current };
      for (const item of items) {
        const key = getRowKey(item);
        if (checked) next[key] = item;
        else delete next[key];
      }
      return next;
    });
  }

  async function deleteSelectedRows() {
    const rows = Object.values(selectedRows);
    if (!rows.length) return;
    if (!window.confirm(`${rows.length} seçili stok kaydını silmek istediğinize emin misiniz?`)) return;

    setDeleting(true);
    try {
      const results = await Promise.allSettled(
        rows.map((item) => {
          const qty = Number(item.on_hand_qty || 0);
          if (!Number.isFinite(qty) || qty <= 0) {
            return Promise.reject(new Error(`${item.lot_no || "Lot"} için geçerli stok miktarı yok`));
          }
          return adjustInventory({
            material_id: Number(item.material_id),
            material_variant_id: item.material_variant_id == null ? null : Number(item.material_variant_id),
            lot_no: item.lot_no,
            qty: -qty,
            note: "Envanterden silindi",
          });
        })
      );
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length) {
        alert(`${rows.length - failed.length} kayıt silindi, ${failed.length} kayıt silinemedi.`);
      } else {
        alert(`${rows.length} stok kaydı silindi.`);
      }
      setSelectedRows({});
      loadBalances();
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    filterChangedRef.current = true;
    setPage(0);
  }, [
    filterMaterialId,
    filterVariantId,
    filterColor,
    filterCustomer,
    filterSupplier,
    filterLotNo,
    filterInboundDocNo,
    filterSalesOrderNo,
    filterWorkOrderNo,
    filterBoxWidth,
    filterBoxLength,
    filterBoxHeight,
    filterPaperGrammage,
    filterPaperType,
  ]);

  useEffect(() => {
    loadBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    filterMaterialId,
    filterVariantId,
    filterColor,
    filterCustomer,
    filterSupplier,
    filterLotNo,
    filterInboundDocNo,
    filterSalesOrderNo,
    filterWorkOrderNo,
    filterBoxWidth,
    filterBoxLength,
    filterBoxHeight,
    filterPaperGrammage,
    filterPaperType,
  ]);

  async function pickVariantForFilter(mid) {
    const template = await getMaterialVariantDimensions(mid);
    if (!template?.length) {
      alert("Bu malzeme için varyant şablonu yok.");
      return;
    }
    setFilterVariantTemplate(template || []);
    setFilterVariantModalOpen(true);
  }

  const filterMaterial = filterMaterialId
    ? materials.find((m) => Number(m.id) === Number(filterMaterialId))
    : null;

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1540, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
      <PageHeader title="Stok / Envanter" subtitle="Lot bazında stok bakiyesi görüntüleyin." />

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2.25 }, overflow: "hidden", borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
          Stok Bakiyeleri
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ md: "center" }} useFlexGap flexWrap="wrap">
          <Box sx={{ minWidth: { xs: "100%", md: 260 }, flex: { md: "1 1 260px" }, maxWidth: { md: 390 } }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.5,
                color: "text.secondary",
                fontSize: "0.68rem",
              }}
            >
              Malzeme (tümü)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                setMaterialDropdownAnchor(e.currentTarget);
                setMaterialDropdownOpen(true);
                setMaterialDropdownPage(0);
              }}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                height: 36,
                width: "100%",
                fontSize: "0.78rem",
                borderColor: "rgba(0, 0, 0, 0.23)",
                "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)", bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              {filterMaterialId
                ? (() => {
                    const m = materials.find((x) => String(x.id) === String(filterMaterialId));
                    return m ? `${m.code} - ${m.name}` : "Tümü";
                  })()
                : "Tümü"}
            </Button>
            <Popover
              open={materialDropdownOpen}
              anchorEl={materialDropdownAnchor}
              onClose={() => setMaterialDropdownOpen(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 340 } } }}
            >
              <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                <ListItemButton
                  selected={!filterMaterialId}
                  onClick={() => {
                    setFilterMaterialId("");
                    setFilterVariantId(undefined);
                    setFilterVariantDisplay("");
                    setPage(0);
                    setMaterialDropdownOpen(false);
                  }}
                >
                  <ListItemText primary="Tümü" primaryTypographyProps={{ fontStyle: "italic" }} />
                </ListItemButton>
                {materials
                  .slice(
                    materialDropdownPage * MATERIALS_PER_DROPDOWN_PAGE,
                    (materialDropdownPage + 1) * MATERIALS_PER_DROPDOWN_PAGE
                  )
                  .map((m) => (
                    <ListItemButton
                      key={m.id}
                      selected={String(filterMaterialId) === String(m.id)}
                      onClick={() => {
                        setFilterMaterialId(String(m.id));
                        setFilterVariantId(undefined);
                        setFilterVariantDisplay("");
                        setPage(0);
                        setMaterialDropdownOpen(false);
                      }}
                    >
                      <ListItemText primary={`${m.code} - ${m.name}`} />
                    </ListItemButton>
                  ))}
              </Box>
              {materials.length > MATERIALS_PER_DROPDOWN_PAGE ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    py: 1,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    size="small"
                    disabled={materialDropdownPage === 0}
                    onClick={() => setMaterialDropdownPage(0)}
                  >
                    ‹ Önceki
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Sayfa {materialDropdownPage + 1} / {Math.ceil(materials.length / MATERIALS_PER_DROPDOWN_PAGE)}
                  </Typography>
                  <Button
                    size="small"
                    disabled={
                      materialDropdownPage >= Math.ceil(materials.length / MATERIALS_PER_DROPDOWN_PAGE) - 1
                    }
                    onClick={() => setMaterialDropdownPage((p) => p + 1)}
                  >
                    Sonraki ›
                  </Button>
                </Box>
              ) : null}
            </Popover>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilterPanelOpen(true)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#818cf8", color: "#4338ca", "&:hover": { borderColor: "#4f46e5", bgcolor: "#eef2ff" } }}
          >
            Varyant / Ozellik Sec
          </Button>
          <Button variant="contained" size="small" onClick={loadBalances} disabled={loading} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", boxShadow: "none", "&:hover": { bgcolor: "#3730a3", boxShadow: "none" } }}>
            Yenile
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setExcelDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#059669", boxShadow: "none", "&:hover": { bgcolor: "#047857", boxShadow: "none" } }}
          >
            Excel Oluştur
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={deleteSelectedRows}
            disabled={!canDeleteStock || deleting || !Object.keys(selectedRows).length}
            title={!canDeleteStock ? "Stoktan silme sadece depocu rolüne özeldir." : undefined}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
          >
            {deleting ? "Siliniyor..." : `Seçilenleri Sil${Object.keys(selectedRows).length ? ` (${Object.keys(selectedRows).length})` : ""}`}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => {
              setFilterMaterialId("");
              setFilterVariantId(undefined);
              setFilterVariantDisplay("");
              setFilterColor("");
              setFilterCustomer("");
              setFilterSupplier("");
              setFilterLotNo("");
              setFilterInboundDocNo("");
              setFilterSalesOrderNo("");
              setFilterWorkOrderNo("");
              setFilterBoxWidth("");
              setFilterBoxLength("");
              setFilterBoxHeight("");
              setFilterPaperGrammage("");
              setFilterPaperType("");
            }}
          >
            Filtreleri Temizle
          </Button>

          {filterVariantDisplay ? (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }} noWrap>
              Seçilen: <b>{filterVariantDisplay}</b>
            </Typography>
          ) : null}
        </Stack>

        {loading ? <LinearProgress sx={{ mt: 2 }} /> : null}

        {!items.length ? (
          <Stack sx={{ mt: 2 }}>
            <EmptyState title="Kayıt yok" description="Filtre kriterine göre stok kaydı bulunamadı." />
          </Stack>
        ) : (
          <TableContainer sx={{ mt: 1.5, width: "100%", overflowX: "hidden", bgcolor: "#fff", border: "1px solid #ddd6fe", borderRadius: 3 }}>
          <Table size="small" sx={inventoryTableSx}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: "3%" }}>
                  <Checkbox
                    size="small"
                    checked={items.length > 0 && items.every((item) => !!selectedRows[getRowKey(item)])}
                    indeterminate={items.some((item) => !!selectedRows[getRowKey(item)]) && !items.every((item) => !!selectedRows[getRowKey(item)])}
                    onChange={(event) => toggleCurrentPage(event.target.checked)}
                  />
                </TableCell>
                {/* Genişlikler toplamı %100 olmalı (tableLayout: fixed); aksi halde
                    artan alan kolonlara dağılıp aralarda boşluk oluşuyor. */}
                <TableCell sx={{ width: "20%" }}>Malzeme</TableCell>
                <TableCell sx={{ width: "12%" }}>Lot</TableCell>
                <TableCell sx={{ width: "7%" }}>Müşteri</TableCell>
                <TableCell sx={{ width: "5%" }}>Sipariş</TableCell>
                <TableCell sx={{ width: "6%" }}>İş emri</TableCell>
                <TableCell sx={{ width: "8%" }}>Tedarikçi</TableCell>
                <TableCell sx={{ width: "8%" }}>Giriş belge</TableCell>
                <TableCell sx={{ width: "8%" }}>Giriş tarihi</TableCell>
                <TableCell align="right" sx={{ width: "6%" }}>Stok 1</TableCell>
                <TableCell sx={{ width: "6%" }}>Birim 1</TableCell>
                <TableCell align="right" sx={{ width: "6%" }}>Stok 2</TableCell>
                <TableCell sx={{ width: "5%" }}>Birim 2</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((x) => {
                // Determine secondary unit value & label per MD strategy
                // Raw Coil → Unit2 = kg (weight from coil_properties)
                // Materials without coil_weight_kg → Unit2 = "–"
                let unit2Value = null;
                let unit2Label = "–";
                if (x.coil_weight_kg !== null && x.coil_weight_kg !== undefined && Number(x.coil_weight_kg) >= 0) {
                  unit2Value = Number(x.coil_weight_kg);
                  unit2Label = "kg";
                }

                return (
                  <TableRow key={`${x.material_id}:${x.material_variant_id || 0}:${x.lot_no || ""}`} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={!!selectedRows[getRowKey(x)]}
                        onChange={(event) => toggleRow(x, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      {String(x.raw_material_type || "") === "paper" && x.paper_kind_label && x.paper_kind_code
                        ? String(x.material_name || "").replace(
                          `Cins:${String(x.paper_kind_code)}`,
                          `Cins:${String(x.paper_kind_label)}`
                        )
                        : x.material_name}
                    </TableCell>
                    <TableCell>{displayLotNo(x.lot_no)}</TableCell>
                    <TableCell>{x.wo_customer_name || x.for_customer_name || x.box_customer_name || x.ink_customer_name || "-"}</TableCell>
                    <TableCell>{x.for_sales_order_no || "-"}</TableCell>
                    <TableCell>{x.work_order_no || "-"}</TableCell>
                    <TableCell>{x.supplier_name || ""}</TableCell>
                    <TableCell>{x.inbound_doc_no || ""}</TableCell>
                    <TableCell>{formatDateOnly(x.received_at)}</TableCell>
                    <TableCell align="right">{stockQtyCell(x)}</TableCell>
                    <TableCell>{displayUnit(x.lot_no, x.material_unit)}</TableCell>
                    <TableCell align="right">
                      {unit2Value !== null ? (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                  {Math.round(Number(unit2Value))}
                        </Typography>
                      ) : (
                        <Typography variant="body2" component="span" color="text.disabled">
                          –
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component="span"
                        color={unit2Label === "–" ? "text.disabled" : "text.primary"}
                      >
                        {unit2Label}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            {total > ITEMS_PER_PAGE ? (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={ITEMS_PER_PAGE}
                    rowsPerPageOptions={[ITEMS_PER_PAGE]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                    colSpan={13}
                    sx={{ borderBottom: "none" }}
                  />
                </TableRow>
              </TableFooter>
            ) : null}
          </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #c7d2fe",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)", borderBottom: "1px solid #c7d2fe" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#fff" }}>
            Varyant / Özellik Seç
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.80)" }}>
            Stok kayıtlarını özelliklerine göre daraltın.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: "#f7f8fc", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
          <Stack spacing={1.5}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid #c7d2fe", borderRadius: 3, backgroundColor: "#f5f3ff" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: "#3730a3" }}>
                Varyant seçimi
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Button
                variant="outlined"
                size="small"
                disabled={!filterMaterialId}
                onClick={() => {
                  if (!filterMaterialId) return;
                  setFilterPanelOpen(false);
                  pickVariantForFilter(Number(filterMaterialId));
                }}
              >
                Tam Varyant Seç
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                disabled={!filterVariantId}
                onClick={() => {
                  setFilterVariantId(undefined);
                  setFilterVariantDisplay("");
                }}
              >
                Varyantı Temizle
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: filterVariantDisplay ? 700 : 400 }}>
                {filterVariantDisplay ? `Seçilen: ${filterVariantDisplay}` : "Tam varyant seçimi için önce malzeme seçin."}
              </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid #c7d2fe", borderRadius: 3, backgroundColor: "#fff" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: "#4338ca" }}>
                Özellik filtreleri
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
              <TextField
                size="small"
                label="Renk"
                value={filterColor}
                onChange={(e) => setFilterColor(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />
              <TextField
                size="small"
                label="Müşteri"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 220 } }}
              />
              <TextField
                size="small"
                label="Tedarikçi"
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 190 } }}
              />
              <TextField
                size="small"
                label="Lot No"
                value={filterLotNo}
                onChange={(e) => setFilterLotNo(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 160 } }}
              />
              <TextField
                size="small"
                label="Giriş Belge No"
                value={filterInboundDocNo}
                onChange={(e) => setFilterInboundDocNo(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />
              <TextField
                size="small"
                label="Sipariş No"
                value={filterSalesOrderNo}
                onChange={(e) => setFilterSalesOrderNo(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 170 } }}
              />
              <TextField
                size="small"
                label="İş Emri No"
                value={filterWorkOrderNo}
                onChange={(e) => setFilterWorkOrderNo(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 170 } }}
              />
              <TextField
                size="small"
                label="Gramaj"
                value={filterPaperGrammage}
                onChange={(e) => setFilterPaperGrammage(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 130 } }}
              />
              <TextField
                size="small"
                label="Kağıt Tipi"
                value={filterPaperType}
                onChange={(e) => setFilterPaperType(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />
              <TextField
                size="small"
                label="Koli En"
                value={filterBoxWidth}
                onChange={(e) => setFilterBoxWidth(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 120 } }}
              />
              <TextField
                size="small"
                label="Koli Boy"
                value={filterBoxLength}
                onChange={(e) => setFilterBoxLength(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 120 } }}
              />
              <TextField
                size="small"
                label="Koli Yükseklik"
                value={filterBoxHeight}
                onChange={(e) => setFilterBoxHeight(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 150 } }}
              />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, backgroundColor: "#ffffff", borderTop: "1px solid #e5e7eb" }}>
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setFilterVariantId(undefined);
              setFilterVariantDisplay("");
              setFilterColor("");
              setFilterCustomer("");
              setFilterSupplier("");
              setFilterLotNo("");
              setFilterInboundDocNo("");
              setFilterSalesOrderNo("");
              setFilterWorkOrderNo("");
              setFilterBoxWidth("");
              setFilterBoxLength("");
              setFilterBoxHeight("");
              setFilterPaperGrammage("");
              setFilterPaperType("");
            }}
          >
            Temizle
          </Button>
          <Button variant="contained" size="small" onClick={() => setFilterPanelOpen(false)}>
            Uygula
          </Button>
        </DialogActions>
      </Dialog>

      {filterVariantModalOpen && filterMaterialId ? (
        <MaterialVariantSelectModal
          materialId={Number(filterMaterialId)}
          materialName={filterMaterial?.name || ""}
          templateDimensions={filterVariantTemplate}
          onClose={() => setFilterVariantModalOpen(false)}
          onResolved={({ material_variant_id, display_name }) => {
            setFilterVariantId(Number(material_variant_id));
            setFilterVariantDisplay(display_name || "");
            setFilterVariantModalOpen(false);
          }}
        />
      ) : null}

      <StockCountExcelDialog open={excelDialogOpen} onClose={() => setExcelDialogOpen(false)} />
    </Stack>
  );
}
